import { useEffect, useMemo, useState, Fragment } from "react";
import { api } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ChartBar, Clock, Timer, Star, Trophy, ArrowsClockwise, User as UserIcon, CaretDown, CaretUp, CalendarBlank, Eraser } from "@phosphor-icons/react";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

const fmtMin = (m) => {
  if (m == null) return "—";
  if (m < 60) return `${Math.round(m)} min`;
  if (m < 60 * 24) return `${(m / 60).toFixed(1)} h`;
  return `${(m / (60 * 24)).toFixed(1)} j`;
};

const MONTHS = ["Janvier","Février","Mars","Avril","Mai","Juin","Juillet","Août","Septembre","Octobre","Novembre","Décembre"];

const rateColor = (r) => {
  if (r == null) return "text-muted-foreground";
  if (r >= 90) return "text-emerald-600 dark:text-emerald-400";
  if (r >= 70) return "text-blue-600 dark:text-blue-400";
  if (r >= 50) return "text-amber-600 dark:text-amber-400";
  return "text-red-600 dark:text-red-400";
};

const Cell = ({ children, className = "" }) => (
  <td className={`px-3 py-3 align-middle text-sm ${className}`}>{children}</td>
);

const HCell = ({ children, title, className = "" }) => (
  <th className={`px-3 py-2 text-left text-[0.65rem] uppercase tracking-wider text-muted-foreground font-semibold ${className}`} title={title}>
    {children}
  </th>
);

const KpiCard = ({ icon, label, value, sub, tone = "primary" }) => {
  const tones = {
    primary: "from-blue-500/10 to-blue-500/0 border-blue-500/20",
    good: "from-emerald-500/10 to-emerald-500/0 border-emerald-500/20",
    warn: "from-amber-500/10 to-amber-500/0 border-amber-500/20",
    critical: "from-red-500/10 to-red-500/0 border-red-500/20",
  };
  return (
    <Card className={`border ${tones[tone] || tones.primary} bg-gradient-to-br`}>
      <CardContent className="pt-5 pb-5">
        <div className="flex items-center gap-2 text-muted-foreground text-xs uppercase tracking-wider mb-1">
          {icon}
          {label}
        </div>
        <div className="text-3xl font-display font-bold leading-tight">{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
      </CardContent>
    </Card>
  );
};

export default function TechStats() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [sortBy, setSortBy] = useState("total");
  const [expanded, setExpanded] = useState({});
  const nowD = new Date();
  const [year, setYear] = useState("all"); // "all" | number
  const [month, setMonth] = useState("all"); // "all" | 1..12
  const [resetting, setResetting] = useState(false);
  // v270 — individual survey responses per tech (loaded once per period).
  const [surveys, setSurveys] = useState([]);

  const load = () => {
    const params = {};
    if (year !== "all") params.year = year;
    if (month !== "all" && year !== "all") params.month = month;
    setRows(null);
    setSurveys([]);
    Promise.all([
      api.get("/stats/tech-performance", { params }),
      api.get("/stats/satisfaction-responses", { params }).catch(() => ({ data: [] })),
    ]).then(([tp, sr]) => {
      setRows(tp.data || []);
      setSurveys(sr.data || []);
    }).catch(() => { setRows([]); setSurveys([]); });
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);

  const yearOptions = useMemo(() => {
    const y = nowD.getFullYear();
    return [y, y - 1, y - 2, y - 3];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetFilters = () => {
    setYear("all");
    setMonth("all");
    setSortBy("total");
    setExpanded({});
  };

  const resetSatisfaction = async () => {
    const scope = year === "all"
      ? "TOUS les sondages historiques (toutes périodes)"
      : month === "all"
        ? `les sondages de l'année ${year}`
        : `les sondages de ${MONTHS[month - 1]} ${year}`;
    if (!window.confirm(`Effacer ${scope} ?\n\nNotes : score, réponses par question et commentaire seront remis à zéro. Les tokens sont conservés — les clients peuvent toujours répondre à nouveau. Les tickets eux-mêmes ne sont PAS supprimés.`)) return;
    setResetting(true);
    try {
      const body = {};
      if (year !== "all") body.year = year;
      if (year !== "all" && month !== "all") body.month = month;
      const r = await api.post("/stats/reset-satisfaction", body);
      toast.success(`${r.data.reset} sondage(s) réinitialisé(s)`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Réinitialisation impossible");
    } finally { setResetting(false); }
  };

  const toggle = (id) => setExpanded((s) => ({ ...s, [id]: !s[id] }));

  if (rows === null) return <div className="text-muted-foreground">Chargement…</div>;

  const sorted = [...rows].sort((a, b) => {
    const A = a[sortBy] ?? -Infinity;
    const B = b[sortBy] ?? -Infinity;
    if (typeof A === "string") return A.localeCompare(B, "fr");
    return B - A;
  });

  const totalTickets = rows.reduce((s, r) => s + (r.total || 0), 0);
  const totalResolved = rows.reduce((s, r) => s + (r.resolved || 0) + (r.closed || 0), 0);
  const avgFrt = rows.filter((r) => r.avg_first_response_min != null);
  const avgFrtVal = avgFrt.length ? avgFrt.reduce((s, r) => s + r.avg_first_response_min, 0) / avgFrt.length : null;
  const avgRes = rows.filter((r) => r.avg_resolution_min != null);
  const avgResVal = avgRes.length ? avgRes.reduce((s, r) => s + r.avg_resolution_min, 0) / avgRes.length : null;
  const sats = rows.filter((r) => r.satisfaction_avg != null);
  const satAvg = sats.length ? sats.reduce((s, r) => s + r.satisfaction_avg * r.satisfaction_count, 0) / (sats.reduce((s, r) => s + r.satisfaction_count, 0) || 1) : null;

  return (
    <div className="space-y-8">
      <header>
        <div className="label-caps">Performance équipe</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold mt-1 flex items-center gap-2">
          <ChartBar size={32} weight="duotone" className="text-blue-500"/> Statistiques techniciens
        </h1>
        <p className="text-muted-foreground mt-2">Taux de prise en charge, délais, réactivité, respect des SLA et satisfaction — par technicien.</p>
      </header>

      {/* Filters — admin/tech scope */}
      <Card>
        <CardContent className="pt-5 pb-5 flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 text-muted-foreground">
            <CalendarBlank size={16}/>
            <span className="text-xs uppercase tracking-wider">Période</span>
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-wider text-muted-foreground block mb-0.5">Année</label>
            <select value={year} onChange={(e) => { const v = e.target.value; setYear(v === "all" ? "all" : Number(v)); if (v === "all") setMonth("all"); }}
              data-testid="techstats-year"
              className="text-sm border rounded-md px-2 py-1.5 bg-background min-w-[110px]">
              <option value="all">Toutes</option>
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[0.65rem] uppercase tracking-wider text-muted-foreground block mb-0.5">Mois</label>
            <select value={month} onChange={(e) => setMonth(e.target.value === "all" ? "all" : Number(e.target.value))}
              disabled={year === "all"}
              data-testid="techstats-month"
              className="text-sm border rounded-md px-2 py-1.5 bg-background min-w-[140px] disabled:opacity-50 disabled:cursor-not-allowed">
              <option value="all">Tous</option>
              {MONTHS.map((label, i) => <option key={i} value={i + 1}>{label}</option>)}
            </select>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {year === "all"
                ? "Vue globale (toutes périodes)"
                : month === "all"
                  ? `Année ${year}`
                  : `${MONTHS[month - 1]} ${year}`}
            </span>
            <Button variant="secondary" size="sm" onClick={resetFilters} data-testid="techstats-reset">
              <ArrowsClockwise size={14} className="mr-1.5"/> Réinitialiser filtres
            </Button>
            {user?.role === "admin" && (
              <Button variant="destructive" size="sm" onClick={resetSatisfaction} disabled={resetting}
                data-testid="techstats-reset-surveys"
                title="Effacer les scores de satisfaction (utile après tests)">
                <Eraser size={14} className="mr-1.5"/>{resetting ? "…" : "Effacer sondages"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Global KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard icon={<Trophy size={14}/>} label="Tickets affectés" value={totalTickets} sub={`${totalResolved} résolus/fermés`}/>
        <KpiCard icon={<Timer size={14}/>} label="Délai moyen de prise en charge" value={fmtMin(avgFrtVal)} tone="primary"/>
        <KpiCard icon={<Clock size={14}/>} label="Délai moyen de résolution" value={fmtMin(avgResVal)} tone="warn"/>
        <KpiCard icon={<Star size={14} weight="fill"/>} label="Satisfaction moyenne" value={satAvg != null ? `${satAvg.toFixed(2)} / 5` : "—"} tone="good"
          sub={sats.reduce((s, r) => s + r.satisfaction_count, 0) + " avis"}/>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="font-display flex items-center gap-2"><UserIcon size={20}/> Détail par technicien</CardTitle>
          <div className="flex items-center gap-2 text-xs">
            <span className="text-muted-foreground">Trier par</span>
            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
              data-testid="techstats-sort"
              className="text-xs border rounded-md px-2 py-1 bg-background">
              <option value="total">Volume</option>
              <option value="resolution_rate">Taux de résolution</option>
              <option value="response_rate">Taux de prise en charge</option>
              <option value="sla_response_rate">SLA prise en charge</option>
              <option value="sla_resolution_rate">SLA résolution</option>
              <option value="avg_first_response_min">Délai réponse</option>
              <option value="avg_resolution_min">Délai résolution</option>
              <option value="satisfaction_avg">Satisfaction</option>
              <option value="name">Nom</option>
            </select>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="techstats-table">
              <thead className="border-b bg-muted/40">
                <tr>
                  <HCell>Technicien</HCell>
                  <HCell title="Nombre total de tickets affectés">Affectés</HCell>
                  <HCell title="Tickets ouverts / en cours">Ouverts</HCell>
                  <HCell title="Tickets résolus + fermés">Résolus</HCell>
                  <HCell title="% de tickets ayant reçu une première réponse du tech">Réactivité</HCell>
                  <HCell title="% de tickets résolus/fermés">Taux résolution</HCell>
                  <HCell title="Temps moyen avant première réponse">Délai réponse</HCell>
                  <HCell title="Temps moyen entre création et résolution">Délai résolution</HCell>
                  <HCell title="% de tickets où la première réponse a respecté le SLA">SLA réponse</HCell>
                  <HCell title="% de tickets résolus avant échéance SLA">SLA résolution</HCell>
                  <HCell title="Note moyenne (nombre d'avis)">Satisfaction</HCell>
                  <HCell title="Total du temps facturable saisi">Temps saisi</HCell>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r) => (
                  <Fragment key={r.id}>
                  <tr className="border-b hover:bg-muted/20" data-testid={`techstats-row-${r.id}`}>
                    <Cell>
                      <button onClick={() => toggle(r.id)}
                        className="flex items-center gap-2 min-w-[180px] hover:text-primary text-left"
                        data-testid={`techstats-expand-${r.id}`}>
                        {r.question_stats?.length > 0 ? (
                          expanded[r.id] ? <CaretUp size={14}/> : <CaretDown size={14}/>
                        ) : <span className="w-[14px] shrink-0"/>}
                        <div className="w-8 h-8 rounded-full bg-blue-600 text-white flex items-center justify-center text-xs font-semibold overflow-hidden shrink-0">
                          {r.photo_url ? <img src={r.photo_url} alt="" className="w-full h-full object-cover"/> : (r.name?.[0]?.toUpperCase() || "?")}
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium truncate">{r.name}</div>
                          <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">{r.role}</div>
                        </div>
                      </button>
                    </Cell>
                    <Cell className="mono font-medium">{r.total}</Cell>
                    <Cell className="mono">{r.open + r.in_progress + r.waiting}</Cell>
                    <Cell className="mono">{r.resolved + r.closed}</Cell>
                    <Cell className={`mono font-semibold ${rateColor(r.response_rate)}`}>
                      {r.response_rate != null ? `${r.response_rate}%` : "—"}
                      <div className="text-[0.65rem] text-muted-foreground font-normal">{r.responded_count}/{r.total}</div>
                    </Cell>
                    <Cell className={`mono font-semibold ${rateColor(r.resolution_rate)}`}>
                      {r.resolution_rate != null ? `${r.resolution_rate}%` : "—"}
                    </Cell>
                    <Cell className="mono">
                      {fmtMin(r.avg_first_response_min)}
                      <div className="text-[0.65rem] text-muted-foreground font-normal">méd. {fmtMin(r.median_first_response_min)}</div>
                    </Cell>
                    <Cell className="mono">
                      {fmtMin(r.avg_resolution_min)}
                      <div className="text-[0.65rem] text-muted-foreground font-normal">méd. {fmtMin(r.median_resolution_min)}</div>
                    </Cell>
                    <Cell className={`mono font-semibold ${rateColor(r.sla_response_rate)}`}>
                      {r.sla_response_rate != null ? `${r.sla_response_rate}%` : "—"}
                      <div className="text-[0.65rem] text-muted-foreground font-normal">{r.sla_response_met}/{r.sla_response_total}</div>
                    </Cell>
                    <Cell className={`mono font-semibold ${rateColor(r.sla_resolution_rate)}`}>
                      {r.sla_resolution_rate != null ? `${r.sla_resolution_rate}%` : "—"}
                      <div className="text-[0.65rem] text-muted-foreground font-normal">{r.sla_resolution_met}/{r.sla_resolution_total}</div>
                    </Cell>
                    <Cell className="mono">
                      {r.satisfaction_avg != null ? (
                        <span className="inline-flex items-center gap-1">
                          <Star size={12} weight="fill" className="text-amber-500"/>
                          {r.satisfaction_avg.toFixed(2)}
                          <span className="text-[0.65rem] text-muted-foreground">({r.satisfaction_count})</span>
                        </span>
                      ) : "—"}
                    </Cell>
                    <Cell className="mono text-muted-foreground">{r.time_spent_hours ? `${r.time_spent_hours} h` : "—"}</Cell>
                  </tr>
                  {expanded[r.id] && (r.question_stats?.length > 0 || surveys.some((s) => s.assignee_id === r.id)) && (
                    <tr className="bg-muted/20 border-b" data-testid={`techstats-detail-${r.id}`}>
                      <td colSpan={12} className="px-6 py-4 space-y-5">
                        {r.question_stats?.length > 0 && (
                          <div>
                            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
                              <Star size={12} weight="fill" className="text-amber-500"/> Détail par question
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                              {r.question_stats.map((qs) => (
                                <div key={qs.id} className="p-3 rounded-md bg-background border">
                                  <div className="text-sm font-medium truncate" title={qs.label}>{qs.label}</div>
                                  <div className="mt-1 flex items-baseline gap-2">
                                    <span className="font-display text-2xl font-bold">
                                      {qs.avg != null ? qs.avg.toFixed(2) : "—"}
                                    </span>
                                    <span className="text-xs text-muted-foreground">/ 5</span>
                                    <span className="ml-auto text-xs text-muted-foreground">({qs.count} avis)</span>
                                  </div>
                                  <div className="mt-2 flex gap-0.5">
                                    {[1,2,3,4,5].map((s) => (
                                      <Star key={s} size={12}
                                        weight={qs.avg != null && qs.avg >= s - 0.5 ? "fill" : "regular"}
                                        className={qs.avg != null && qs.avg >= s - 0.5 ? "text-amber-500" : "text-muted-foreground/40"}/>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        {(() => {
                          const mySurveys = surveys.filter((s) => s.assignee_id === r.id);
                          if (mySurveys.length === 0) return null;
                          return (
                            <div data-testid={`techstats-surveys-${r.id}`}>
                              <div className="text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1">
                                <Star size={12} weight="fill" className="text-amber-500"/> Détail des sondages ({mySurveys.length})
                              </div>
                              <div className="rounded-md border bg-background overflow-x-auto">
                                <table className="w-full text-sm">
                                  <thead className="bg-muted/40 text-xs">
                                    <tr>
                                      <th className="text-left px-3 py-2">Date</th>
                                      <th className="text-left px-3 py-2">Ticket</th>
                                      <th className="text-left px-3 py-2">Client</th>
                                      <th className="text-left px-3 py-2">Note</th>
                                      <th className="text-left px-3 py-2">Commentaire</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {mySurveys.map((s) => (
                                      <tr key={s.ticket_id} className="border-t" data-testid={`survey-row-${s.ticket_id}`}>
                                        <td className="px-3 py-2 text-xs text-muted-foreground font-mono whitespace-nowrap">
                                          {s.submitted_at ? new Date(s.submitted_at).toLocaleDateString("fr-FR") : "—"}
                                        </td>
                                        <td className="px-3 py-2">
                                          <a href={`/tickets/${s.ticket_id}`}
                                            className="text-primary hover:underline font-mono text-xs"
                                            data-testid={`survey-ticket-${s.ticket_id}`}>
                                            #{s.ticket_number}
                                          </a>
                                          <div className="text-[11px] text-muted-foreground truncate max-w-[220px]" title={s.subject}>{s.subject}</div>
                                        </td>
                                        <td className="px-3 py-2">
                                          <div className="font-medium">{s.client_name}</div>
                                          {s.client_email && <div className="text-[11px] text-muted-foreground">{s.client_email}</div>}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap">
                                          <span className="inline-flex items-center gap-1 font-semibold">
                                            <Star size={12} weight="fill" className="text-amber-500"/> {s.score}/5
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-xs">
                                          {s.comment ? (
                                            <span className="italic text-muted-foreground line-clamp-2 max-w-[320px] block" title={s.comment}>« {s.comment} »</span>
                                          ) : <span className="text-muted-foreground/60">—</span>}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  )}
                  </Fragment>
                ))}
                {sorted.length === 0 && (
                  <tr><td colSpan={12} className="text-center py-8 text-muted-foreground">Aucun technicien affecté.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground space-y-1">
        <div className="flex items-center gap-2"><ArrowsClockwise size={12}/> <span>Actualisez la page pour recalculer.</span></div>
        <div><b>Réactivité</b> = % de tickets où le technicien a envoyé une première réponse (visible client). <b>SLA réponse/résolution</b> = respect de l&apos;échéance calculée à partir de la priorité.</div>
      </div>
    </div>
  );
}
